import { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { PuzzleMark } from "@/components/puzzle/PuzzleMark";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Solutions", href: "#solutions" },
  { label: "Process", href: "#process" },
  { label: "Contact", href: "#contact" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const toggleRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ESC close + body lock
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleNav = useCallback((e, href) => {
    e.preventDefault();
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 72;
      window.scrollTo({ top, behavior: "smooth" });
    }
    setOpen(false);
  }, []);

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className="fixed top-0 left-0 right-0 z-50"
        style={{
          background: scrolled ? "rgba(10,10,10,0.85)" : "rgba(10,10,10,0.55)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          borderBottom: scrolled
            ? "1px solid rgba(192,192,192,0.18)"
            : "1px solid rgba(192,192,192,0.06)",
          transition: "background 0.3s ease, border 0.3s ease",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 h-16 md:h-[72px] flex items-center justify-between">
          {/* Brand */}
          <a
            href="#hero"
            onClick={(e) => handleNav(e, "#hero")}
            data-testid="nav-brand"
            className="flex items-center gap-3 group min-w-0"
            aria-label="Krister Overseas — home"
          >
            <PuzzleMark size={34} animated={false} />
            <div className="hidden xl:flex flex-col leading-tight min-w-0">
              <span
                style={{
                  fontFamily: "Montserrat",
                  fontWeight: 900,
                  fontSize: 13,
                  letterSpacing: "0.18em",
                  color: "#F5F1E8",
                  whiteSpace: "nowrap",
                }}
              >
                KRISTER OVERSEAS
              </span>
              <span
                className="ko-overline"
                style={{ color: "#B89B5E", fontSize: 9, whiteSpace: "nowrap" }}
              >
                Branding Beyond Boundaries
              </span>
            </div>
            <div className="hidden sm:flex lg:hidden flex-col leading-tight">
              <span
                style={{
                  fontFamily: "Montserrat",
                  fontWeight: 900,
                  fontSize: 13,
                  letterSpacing: "0.18em",
                  color: "#F5F1E8",
                  whiteSpace: "nowrap",
                }}
              >
                KRISTER OVERSEAS
              </span>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7 xl:gap-8" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                href={l.href}
                onClick={(e) => handleNav(e, l.href)}
                className="ko-overline relative"
                style={{ color: "#C0C0C0", letterSpacing: "0.2em" }}
              >
                <span className="hover:text-[#F5F1E8] transition-colors duration-300">
                  {l.label}
                </span>
              </a>
            ))}
            <a
              href="#contact"
              data-testid="nav-cta-start-project"
              onClick={(e) => handleNav(e, "#contact")}
              className="ko-overline inline-flex items-center gap-2 px-5 py-3 transition-all duration-300 whitespace-nowrap"
              style={{
                background: "#F5F1E8",
                color: "#0A0A0A",
                letterSpacing: "0.22em",
                fontSize: 11,
                fontWeight: 700,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#B89B5E";
                e.currentTarget.style.color = "#0A0A0A";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#F5F1E8";
                e.currentTarget.style.color = "#0A0A0A";
              }}
            >
              Start a Project
              <span
                aria-hidden
                style={{
                  width: 16,
                  height: 1,
                  background: "currentColor",
                }}
              />
            </a>
          </nav>

          {/* Mobile toggle */}
          <button
            ref={toggleRef}
            data-testid="mobile-menu-toggle"
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav-dialog"
            aria-label={open ? "Close menu" : "Open menu"}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10"
            style={{ color: "#F5F1E8", border: "1px solid rgba(192,192,192,0.25)" }}
          >
            {open ? (
              <X size={18} strokeWidth={1.5} />
            ) : (
              <Menu size={18} strokeWidth={1.5} />
            )}
          </button>
        </div>
      </motion.header>

      {/* Mobile dialog */}
      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-nav-dialog"
            data-testid="mobile-nav-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Main navigation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 lg:hidden"
            style={{ background: "rgba(10,10,10,0.96)", backdropFilter: "blur(14px)" }}
          >
            <motion.nav
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="pt-24 px-6 flex flex-col gap-6"
              aria-label="Mobile primary"
            >
              {NAV_LINKS.map((l, i) => (
                <a
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  href={l.href}
                  onClick={(e) => handleNav(e, l.href)}
                  className="flex items-baseline justify-between border-b py-4"
                  style={{
                    borderColor: "rgba(192,192,192,0.15)",
                    color: "#F5F1E8",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "Montserrat",
                      fontWeight: 700,
                      fontSize: 28,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {l.label}
                  </span>
                  <span
                    className="ko-overline"
                    style={{ color: "#B89B5E", fontSize: 10 }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </a>
              ))}
              <a
                data-testid="mobile-nav-cta-start"
                href="#contact"
                onClick={(e) => handleNav(e, "#contact")}
                className="ko-overline inline-flex items-center justify-center gap-3 px-6 py-4 mt-4"
                style={{
                  background: "#F5F1E8",
                  color: "#0A0A0A",
                  letterSpacing: "0.22em",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Start a Project
              </a>
              <div
                className="ko-overline mt-6"
                style={{ color: "#C0C0C0", fontSize: 10 }}
              >
                contact@kristeroverseas.com · Delhi, India
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
